// @services/stubs/StubFeedbackClient.ts
import type { FeedbackClient } from '@services/clients/FeedbackClient';
import type { SubmitFeedbackRequest, SubmitFeedbackResponse } from "@ps-bridge/models/core/dataContracts";
import type { FeedbackError } from "@ps-bridge/models/core/dataContracts";
import * as E from 'fp-ts/lib/Either';
import type { OptionTs, TaskEitherTs } from '@ps-bridge/interop';
import * as Option from "@utils/Option";

import { ok } from "@ps-bridge/ok";
import type { Ok } from "@ps-bridge/ok";
import { makeErrorMessageText } from "@ps-bridge/models/brands/errorMessageText";
import { taskEitherFromRaw } from '@utils/effects/branded';

const delay = (ms: number): Promise<Ok> =>
    new Promise((resolve) => setTimeout(() => resolve(ok), ms));

const STUB_FEEDBACK_MODE_KEY = "stub:feedback:mode";
const STUB_FEEDBACK_ERROR_TYPE_KEY = "stub:feedback:errorType";
const STUB_FEEDBACK_ERROR_MESSAGE_KEY = "stub:feedback:errorMessage";
const DEFAULT_FEEDBACK_ERROR_TYPE = "NetworkError";
const DEFAULT_FEEDBACK_ERROR_MESSAGE = "Unable to submit feedback.";

const getStubStorageValue = (key: string): OptionTs<string> => {
    if (typeof window === "undefined") {
        return Option.none();
    }
    try {
        const value = window.localStorage.getItem(key);
        return typeof value === "string" ? Option.some(value) : Option.none();
    } catch {
        return Option.none();
    }
};

const getStubFeedbackMode = (): "success" | "error" =>
    Option.fold(
        getStubStorageValue(STUB_FEEDBACK_MODE_KEY),
        () => "success",
        (value) => (value === "error" ? "error" : "success"),
    );

const buildFeedbackError = (): FeedbackError => ({
    type: Option.fold(
        getStubStorageValue(STUB_FEEDBACK_ERROR_TYPE_KEY),
        () => DEFAULT_FEEDBACK_ERROR_TYPE,
        (value) => value,
    ),
    message: makeErrorMessageText(
        Option.fold(
            getStubStorageValue(STUB_FEEDBACK_ERROR_MESSAGE_KEY),
            () => DEFAULT_FEEDBACK_ERROR_MESSAGE,
            (value) => value,
        ),
    ),
    statusCode: Option.none(),
});

export class StubFeedbackClient implements FeedbackClient {
    submitFeedback(
        request: SubmitFeedbackRequest,
    ): TaskEitherTs<FeedbackError, SubmitFeedbackResponse> {
        console.log('StubFeedbackClient.submitFeedback called', request);

        return taskEitherFromRaw(async () => {
            await delay(100);
            if (getStubFeedbackMode() === "error") {
                return E.left(buildFeedbackError());
            }
            return E.right({
                feedback_id: 'stub-feedback-id',
            });
        });
    }
}
